import React, { useContext, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useHeaderFooter, useFacilitatorStatus } from '../../hooks';
import { AppDataContext } from '../../context';
import { UserEditPopUp, Achievements, CoursesInProgress } from '../../components';
import type { AuthenticatedUser } from '../../types';

import './index.scss';
import messages from './messages';

import emailImg from '../../img/email.svg';
import line65Img from '../../img/line-6-5.svg';
import linkedInImg from '../../img/linkedin.svg';
import xImg from '../../img/x-twitter.svg';
import editImg from '../../img/profileLearner/edit2.svg';
import layer4Img from '../../img/profileLearner/layer-4.svg';

interface ProfileLearnerProps {
  children?: React.ReactNode;
}

const ProfileLearner: React.FC<ProfileLearnerProps> = ({ children }) => {
  const { authenticatedUser, setAuthenticatedUser } = useContext(AppDataContext);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false);
  const [badgesEnabled, setBadgesEnabled] = useState<boolean>(true);

  const intl = useIntl();
  const { headerContent, footerContent } = useHeaderFooter(children);
  const { status: facilitatorStatus } = useFacilitatorStatus();
  const {
    name, profileImage, bio, email, socialLinks,
  } = authenticatedUser ?? {};

  return (
    <>
      <input
        type="hidden"
        id="anPageName"
        name="page"
        defaultValue="profile-learner"
      />
      <div className="container-center-horizontal">
        <div className="profile-learner screen">
          <div className="overlap-group8">
            <div className="hero-image">
              <div className="overlap-group">
                <h1 className="learner-saanvi-khatri recklessneue-book-normal-white-100-1px">
                  {intl.formatMessage(messages.learner)}
                  <br />
                  <span className="c-name">{name || ''}</span>
                </h1>
                <img className="layer_4" src={layer4Img} alt="" />
              </div>
            </div>
            {headerContent}
          </div>
          <div className="page-nav page">
            <div className="dashboard-your-profile recklessneue-book-normal-congress-blue-22-8px">
              <NavLink
                id="dashboard-link"
                to="/user-dashboard"
              >
                {intl.formatMessage(messages.dashboard)}
              </NavLink>
              {' '}
              &gt; {intl.formatMessage(messages.yourProfile)}
            </div>
            <img className="line-6 line" src={line65Img} alt="Line 6" />
          </div>
          <div className="mentor-bio">
            <div className="flex-row flex learner-bio-block">
              <div
                className="headshot public-profile-image-container"
                style={{
                  visibility: 'visible',
                  cursor: 'default',
                }}
              >
                <img
                  className="image-9 user-img"
                  src={`${profileImage?.imageUrlFull}`}
                  alt={name}
                />
              </div>
              <div className="overlap-group7">
                <div className="overlap-group-1">
                  <p className="lorem-ipsum-dolor-si-1 neuehaasgroteskdisplaypro-45-light-congress-blue-24px c-bio">
                    {bio}
                  </p>
                  <div className="learner-bio instrumentserif-regular-normal-congress-blue-48px">
                    <span className="instrumentserif-normal-congress-blue-48px">
                      {intl.formatMessage(messages.learner)}{' '}
                    </span>
                    <span className="recklessneue-regular-normal-congress-blue-48px">
                      {intl.formatMessage(messages.bio)}
                    </span>
                    {facilitatorStatus?.is_facilitator && (
                      <span className="facilitator-badge">
                        {intl.formatMessage(messages.facilitatorBadge)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="socials">
                  <a data-name="email" href={`mailto:${email}`}>
                    {' '}
                    <img className="email" src={emailImg} alt="Email" />{' '}
                  </a>
                  {(() => {
                    const xLink = socialLinks?.find((link: { platform: string }) => link?.platform === 'x')?.socialLink
                      || socialLinks?.find((link: { platform: string }) => link?.platform === 'twitter')?.socialLink;
                    return xLink && (
                      <a
                        data-name="x"
                        href={xLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {' '}
                        <img
                          className="x"
                          src={xImg}
                          alt="X.com"
                        />{' '}
                      </a>
                    );
                  })()}
                  {socialLinks?.find((link: { platform: string }) => link?.platform === 'linkedin')?.socialLink && (
                    <a
                      data-name="linkedin"
                      href={socialLinks?.find((link: { platform: string }) => link?.platform === 'linkedin')?.socialLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {' '}
                      <img
                        className="linkedin"
                        src={linkedInImg}
                        alt="Linkedin"
                      />{' '}
                    </a>
                  )}
                  <button
                    type="button"
                    className="learner-profile-edit-button"
                    onClick={() => setIsProfileModalOpen(true)}
                    aria-label={intl.formatMessage(messages.edit)}
                  >
                    <img
                      className="learner-profile-edit edit"
                      src={editImg}
                      alt={intl.formatMessage(messages.edit)}
                      style={{ display: 'inline' }}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* TODO: should be replaced with RGG and default certificates rendering */}
          {badgesEnabled && (
            <div className="badges">
              <div className="badges-container">
                <div className="has-earnt-these-badges recklessneue-book-normal-congress-blue-48px">
                  {intl.formatMessage(messages.hasEarntThisBadges, { lineBreak: <br /> })}
                </div>
                <div className="badges-1">
                  <Achievements displayCertificates={false} setEnabled={setBadgesEnabled} />
                </div>
              </div>
            </div>
          )}

          <CoursesInProgress />

          {footerContent}
          {isProfileModalOpen && (
            <UserEditPopUp
              userMetadata={authenticatedUser}
              setIsProfileModalOpen={setIsProfileModalOpen}
              setUserMetadata={(updatedUser: AuthenticatedUser) => {
                setAuthenticatedUser(updatedUser);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ProfileLearner;
